/**
 * REPLY_TO_COMMENT task template - shows comment to reply to with reply icon
 *
 * @module pages/tasks/components/templates/ReplyToCommentTaskTemplate
 */

import { Text } from '@/components/atoms/Text';
import { TaskDebugPanel } from '@/components/composed/TaskDebugPanel';
import { TargetPreview } from '../previews';
import { TaskActions } from './TaskActions';
import { getStatusColor } from '../../utils';
import type { TaskTemplateProps } from '../../types';

export function ReplyToCommentTaskTemplate({ task, onNavigate, onComplete, onDismiss, onDelete, isUpdating, debugMode }: TaskTemplateProps) {
  return (
    <div className="bg-surface-1 border border-border rounded-lg p-4 hover:border-primary/50 transition-colors">
      <div className="flex items-start justify-between gap-4">
        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Header with reply icon */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
            <span className="text-sm font-medium text-foreground">Reply to Comment</span>
            <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(task.status)}`}>
              {task.status}
            </span>
          </div>

          {/* Description if available */}
          {task.description && (
            <Text className="text-sm mb-3 line-clamp-2">{task.description}</Text>
          )}

          {/* Target Preview - shows the comment and photo */}
          <div className="mb-3">
            <TargetPreview task={task} />
          </div>

          {/* Metadata */}
          <div className="text-xs text-muted">
            <span>{new Date(task.createdAt).toLocaleTimeString()}</span>
          </div>

          {/* Debug Panel */}
          {debugMode && <TaskDebugPanel task={task} />}
        </div>

        {/* Actions */}
        <TaskActions
          task={task}
          onNavigate={onNavigate}
          onComplete={onComplete}
          onDismiss={onDismiss}
          onDelete={onDelete}
          isUpdating={isUpdating}
        />
      </div>
    </div>
  );
}
